import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportCategoryCSV,
  fetchCategories as OnfetchCategorys,
  updateStatusInCategory,
} from "slices/category/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const Category = () => {
  document.title = "Category List";
  const dispatch = useDispatch<any>();
  const location = useLocation();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "category",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  // Redux selector for category state
  const selectCategorys = createSelector(
    (state: any) => state.Category,
    (category) => ({
      categories: category.categoryList,
      rowCount: category.rowCount,
      loading: category.loading,
      error: category.error,
    })
  );
  const { categories, rowCount, loading, error } = useSelector(selectCategorys);
  
  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.category
  );

  const [data, setData] = useState([]);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      OnfetchCategorys({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    colValue,
  ]);

  useEffect(() => {
    setData(categories);
  }, [categories]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Debounce search input to actual search
  // Only update search if searchInput actually changed (not on initial restore)
  useEffect(() => {
    // Skip if searchInput matches search (already in sync, likely from restore)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  const handleStatusToggle = async (pkg: any) => {
    try {
      const updatedStatus = !pkg.is_active;
      const updatedCategory = {
        id: pkg.category_id,
        is_active: updatedStatus,
      };
      // const payload = { is_active: updatedCategory.is_active };
      setIsStatusUpdateLoading(true);
      await dispatch(updateStatusInCategory(updatedCategory));
      // await Axios.patch(`/category/${updatedCategory.id}/update/`, payload);
      toast.success(
        `Category ${updatedStatus ? "activated" : "deactivated"} successfully`
      );

      dispatch(
        OnfetchCategorys({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: colValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await exportCategoryCSV({
        search,
        filters: colValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export failed", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const DynamicAction =
    canUpdate
      ? [
          {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => (
              <EditButton to={`/edit-category/${row?.original?.category_id}`} />
            ),
          },
        ]
      : [];

  const columns = [
    {
      header: "Sr. No.",
      id: "sr_no", // important: since there's no accessorKey
      accessorKey: "sr_no",
      enableSorting: false,
      enableColumnFilter: false,
      // cell: ({ row }: { row: any }) => {
      //   return row.index + 1 + pageIndex * pageSize;
      // },
    },
    {
      header: "Category",
      accessorKey: "category_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: "master/category/dropdown/category_name",
    },

    {
      header: "Status",
      accessorKey: "is_active",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const cat = row.original;
        return (
          <StatusToggle
            isActive={cat.is_active}
            onChange={() => {
              // handleStatusToggle toggles the status internally
              handleStatusToggle(cat);
            }}
            disabled={!canUpdate}
          />
        );
      },
    },
    {
      header: "Remarks",
      // id: "remarks", // used `id` instead of accessorKey since data isn't ready yet
      accessorKey: "remark", //*// use Once API starts returning a remark field
      enableSorting: false,
      enableColumnFilter: false,
      maxSize: 250,
      cell: ({ row }: { row: any }) => (
        <TruncatedTextCell
          text={row?.original?.remark}
          maxWidth="300px"
          maxLines={4}
          rowIndex={row.index}
          tooltipId={`remarks-tooltip-${row.index}`}
        />
      ),
    },
    {
      header: "Created By/Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const name = row.original.created_by_user?.username ?? "N/A";
        const date = row.original.created_at
          ? new Date(row.original.created_at).toISOString().split("T")[0]
          : "N/A";
        return `${name}-${date}`;
      },
    },
    {
      header: "Updated By/Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const name = row.original.updated_by_user?.username ?? "N/A";
        const date = row.original.updated_at
          ? new Date(row.original.updated_at).toISOString().split("T")[0]
          : "N/A";
        return `${name}-${date}`;
      },
    },
    ...DynamicAction,
  ];

  return (
    <ListingPageContainer>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Category</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}

                    {canCreate && (
                      <ActionButton
                        label="Add Category"
                        icon="bi bi-plus-circle"
                        navigateLink="/add-category"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper
                loading={loading || isStatusUpdateLoading}
                error={error}
              >
                <TableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search categories..."
                  manualSorting={true}
                  colValue={setColValue}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Category;
